import "./insWidget.scss";
import "./insWidgetFooter.scss";
import React, { useMemo } from "react";
import { getLabelForPerformancePeriod, useGetDateText } from "../chart/base/utils";
import { usePatchedWidgetComponentProps, useWidgetComponentName, useWidgetCurrentEndDate, useWidgetCurrentPeriodOfAnalysis, useWidgetCurrentStartDate, useWidgetFooterProperties } from "../hooks/use-current-widget";
import { useGetSelectedCurrency } from "components/workspaces/hooks/use-current-dashboard";
import { language } from "language/default";
import preqinIcon from "../icons/preqin.svg";
import { getDateString } from "common/date-utils";
const iconMap = { preqin: preqinIcon };
const renderFooterText = (footer) => {
    if (footer.benchmarkName && footer.text) {
        return React.createElement("span", { className: "insFooterText" }, footer.text);
    }
    if (footer.link && !footer.benchmarkName) {
        return React.createElement("a", { className: "insFooterLink", href: footer.link, target: "_blank", rel: "noopener noreferrer" }, footer.text);
    }
    if (footer.text) {
        return React.createElement("span", { className: "insFooterText" }, footer.text);
    }
    return null;
};
const buildExternalFooterNode = (footer, processedDateText) => {
    if (!footer) {
        return undefined;
    }
    return (React.createElement("div", { className: "insFooter externalFooter" },
        footer.icon ? React.createElement("img", { src: iconMap[footer.icon] ?? footer.icon, className: "insFooterIcon", alt: "" }) : null,
        footer.benchmarkName && footer.link
            ? React.createElement("a", { className: "insFooterLink", href: footer.link, target: "_blank", rel: "noopener noreferrer" }, footer.benchmarkName)
            : null,
        renderFooterText(footer),
        processedDateText ? React.createElement("span", { className: "insFooterDate aux-p13" }, processedDateText) : null));
};
const buildMetaFooterNode = (metaParts) => {
    if (metaParts.length === 0) {
        return null;
    }
    return React.createElement("div", { className: "aux-p13 insFooterMeta" }, metaParts.join(" "));
};
const getMetaParts = (hasExternalFooter, additionalText, notActualData, currencyText, currentPeriodOfAnalysisText, dateRangeText) => {
    if (hasExternalFooter) {
        return [notActualData, currencyText, currentPeriodOfAnalysisText, dateRangeText].filter(Boolean);
    }
    return [additionalText, notActualData, currencyText, currentPeriodOfAnalysisText, dateRangeText].filter(Boolean);
};
const getFooterWithBenchmark = (footer, fundBenchmark) => {
    if (footer?.useFundBenchmark && fundBenchmark?.benchmarkName) {
        return {
            ...footer,
            benchmarkName: fundBenchmark.benchmarkName
        };
    }
    return footer;
};
const getFooterTextValues = (footer, currency) => ({
    additionalText: footer?.text ? footer.text : "",
    currencyText: footer?.showCurrency ? language.en.widget.allInCurrency(currency) : "",
    notActualData: footer?.notActualData ? language.en.widget.notActualData : ""
});
const getDateRangeText = (showRangeText, startDate, endDate) => {
    if (showRangeText && startDate && endDate) {
        return language.en.widget.footer.dateRange(getDateString(startDate), getDateString(endDate));
    }
    return language.en.widget.footer.inceptionDateRange;
};
const shouldShowDateRange = (groupId, widgetComponentName, dateText, hideDateRange) => Boolean(groupId && widgetComponentName !== "InsDatapointGridWidget" && !dateText && !hideDateRange);
const getBenchmarkFromRowData = (footer, rowData) => {
    if (!footer?.benchmarkDatapointField || !rowData || rowData.length === 0) {
        return undefined;
    }
    return rowData[0]?.[footer.benchmarkDatapointField];
};
const buildFooterNodes = (footerWithBenchmark, processedDateText, metaParts) => {
    const externalFooterNode = buildExternalFooterNode(footerWithBenchmark, processedDateText);
    const metaFooterNode = buildMetaFooterNode(metaParts);
    return { externalFooterNode, metaFooterNode };
};
export const InsWidgetFooter = (props) => {
    const { sectionId, widgetId, explicitFooter, groupId, footerRowData } = props;
    const currency = useGetSelectedCurrency(), currentPeriodOfAnalysis = useWidgetCurrentPeriodOfAnalysis(sectionId, widgetId), endDate = useWidgetCurrentEndDate(sectionId, widgetId), footerFromStore = useWidgetFooterProperties(sectionId, widgetId), startDate = useWidgetCurrentStartDate(sectionId, widgetId), widgetComponentName = useWidgetComponentName(sectionId, widgetId);
    const footer = explicitFooter ?? footerFromStore;
    const footerWithBenchmark = useMemo(() => {
        const benchmarkName = getBenchmarkFromRowData(footer, footerRowData);
        const fundBenchmark = benchmarkName ? { benchmarkName } : {};
        return getFooterWithBenchmark(footer, fundBenchmark);
    }, [footer, footerRowData]);
    const currentPeriodOfAnalysisText = currentPeriodOfAnalysis
        ? language.en.widget.footer.periodOfAnalysis(getLabelForPerformancePeriod(currentPeriodOfAnalysis))
        : undefined;
    const { additionalText, currencyText, notActualData } = getFooterTextValues(footerWithBenchmark, currency);
    const processedDateText = useGetDateText(footerWithBenchmark?.dateText, footerWithBenchmark?.additionalData);
    const { hideDateRange } = usePatchedWidgetComponentProps(sectionId, widgetId);
    // Custom dates are still not supported on Grid widgets, so not showing range text there
    const showRangeText = shouldShowDateRange(groupId, widgetComponentName, footerWithBenchmark?.dateText, hideDateRange);
    const dateRangeText = getDateRangeText(showRangeText, startDate, endDate);
    const metaParts = getMetaParts(footer !== undefined, additionalText, notActualData, currencyText, currentPeriodOfAnalysisText, showRangeText ? dateRangeText : "");
    const { externalFooterNode, metaFooterNode } = buildFooterNodes(footerWithBenchmark, processedDateText, metaParts);
    return React.createElement(React.Fragment, null,
        externalFooterNode,
        sectionId && widgetId && metaFooterNode);
};
